"use client";

/**
 * Report figure / metric / scope tile, and the grid that lays them out.
 *
 * Extracted from the design source `compass-oppreport.jsx` (`.rv-tile`,
 * `.rv-tile.static`, `.rv-tiles`, `.rv-tiles.two`, `.rv-tlabel`, `.rv-tval`,
 * `.rv-tper`, `.rv-tshift`).
 */

import { ArrowR } from "../lib/icons";
import { ReportAssumedBadge } from "./ReportAssumedBadge";
import type { ReportMetricShift } from "./report-types";

/* ---------- tile ---------- */

export interface ReportTileProps {
  label: string;
  /** Formatted, or `—`. */
  value: string;
  /** e.g. "per year", rendered small next to the value. */
  valueSuffix?: string;
  /**
   * The value spans a fallback horizon. Renders an "Assumed" badge on the
   * tile's bottom line — never inside `label`, where the clamp can swallow it.
   */
  assumed?: boolean;
  /** Horizon for the badge's tooltip, e.g. "3 years". */
  horizonLabel?: string | null;
  /** Design `.rv-tshift` — the compact "6,000 → 3,000 hours" line. */
  shift?: ReportMetricShift;
  /** Omit ⇒ `.rv-tile.static` (no hover, no cursor). */
  onClick?: () => void;
}

const TILE_BASE = [
  // `min-w-0`: a grid item's default `min-width: auto` lets its content push
  // the tile past its 1fr track, which is how a long value used to spill over
  // the card's right border instead of wrapping inside it.
  "grid gap-1.5 justify-items-start text-left w-full min-w-0",
  "font-[family-name:var(--font-body)]",
  "bg-(--bg-white) border border-(--border-subtle)",
  "rounded-(--radius-container) px-4 py-3.5",
  "transition-colors duration-300 ease-in-out",
].join(" ");

/**
 * `.rv-tlabel` — 10px uppercase caption pinned to the top row.
 *
 * Two lines, clamped, over a fixed 30px row rather than the design's single
 * ellipsed line. Four tiles across a 760px document leave ~133px of label,
 * and real labels run far longer than the design's ("Avoided authorization
 * determination-delay cost" needs 237px) — ellipsing them cuts the noun the
 * card is about. The row is a fixed height so a one-line label and a two-line
 * label still align their values across the grid; `line-clamp-2` keeps a
 * pathological third line from breaking that. `title` carries the full string.
 */
const TILE_LABEL = [
  "text-[10px] leading-[1.35] tracking-[0.08em] uppercase",
  "font-bold text-(--fg-muted) self-start",
  "w-full line-clamp-2",
].join(" ");

/**
 * `.rv-tval` — 22px display number, baseline-aligned with its suffix.
 *
 * Wraps, unlike the design's single line: `paybackLabel` is prose ("about 5
 * months"), not a number, and four tiles across a 760px document leave ~133px
 * of value. `flex-wrap` puts the suffix on its own line rather than forcing
 * the row wider than the card. The value row is `auto` in every tile of a
 * grid row, so a wrapped value still aligns across the row.
 */
const TILE_VALUE = [
  "flex flex-wrap items-baseline self-center w-full min-w-0",
  "text-[22px] leading-[1.15] font-bold tracking-[-0.02em]",
  "text-(--fg-primary)",
].join(" ");

/**
 * A single report tile. Clickable tiles reserve a third grid row for the
 * shift line so every tile in a row keeps the same height; static tiles
 * (design `.rv-tile.static`, used by Scope) drop it and lose their hover.
 */
export function ReportTile({
  label,
  value,
  valueSuffix,
  assumed,
  horizonLabel,
  shift,
  onClick,
}: ReportTileProps) {
  const body = (
    <>
      <span className={TILE_LABEL} title={label}>
        {label}
      </span>
      <span className={TILE_VALUE}>
        {/* Breaks between words, never inside one: "$894K" has no break
            opportunity and stays whole, "about 5 months" wraps. */}
        <span className="min-w-0 break-words">{value}</span>
        {valueSuffix && (
          <span className="ml-1.5 font-[family-name:var(--font-body)] text-[11px] font-medium text-(--fg-muted)">
            {valueSuffix}
          </span>
        )}
      </span>
      {/*
        The tile's last row, pinned to the bottom of the card. The badge lives
        here rather than beside the value so it lands on the same line in every
        tile of a row: inline, it fits after "$3.7K" and wraps after "$199.71M",
        which reads as three different layouts across four tiles.

        Badge and shift never co-occur in practice — a figure has a horizon, a
        metric has a baseline → projected line — but they stack rather than
        collide if that ever changes.
      */}
      {(assumed || shift) && (
        <span className="flex w-full flex-col gap-1.5 self-end">
          {assumed && (
            <span>
              <ReportAssumedBadge horizonLabel={horizonLabel} />
            </span>
          )}
          {/* `.rv-tshift` — the baseline → projected line over its unit. */}
          {shift && (
            <span className="flex flex-col gap-px">
              <span className="inline-flex items-center gap-[5px] whitespace-nowrap font-[family-name:var(--font-mono)] text-[12.5px] leading-[1.3] text-(--fg-secondary)">
                {shift.from}
                <span className="text-(--fg-subtle)">
                  <ArrowR size={11} />
                </span>
                {shift.to}
              </span>
              <span className="text-[11px] leading-[1.3] text-(--fg-muted)">
                {shift.unit}
              </span>
            </span>
          )}
        </span>
      )}
    </>
  );

  if (!onClick) {
    return (
      <div className={[TILE_BASE, "grid-rows-[30px_auto]"].join(" ")}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={[
        TILE_BASE,
        "grid-rows-[30px_auto_1fr] cursor-pointer",
        "hover:border-(--border-strong) hover:bg-(--bg-sky)",
      ].join(" ")}
      onClick={onClick}
    >
      {body}
    </button>
  );
}

/* ---------- grid ---------- */

export interface ReportTileGridProps {
  /** `.rv-tiles` is 4-up, `.rv-tiles.two` is 2-up. */
  columns: 2 | 4;
  children: React.ReactNode;
}

/**
 * The `.rv-tiles` grid. The 4-up variant collapses to 2-up under 1080px,
 * matching the design's media query.
 */
export function ReportTileGrid({ columns, children }: ReportTileGridProps) {
  return (
    <div
      className={[
        "grid gap-2.5",
        columns === 2 ? "grid-cols-2" : "grid-cols-4 max-[1080px]:grid-cols-2",
      ].join(" ")}
    >
      {children}
    </div>
  );
}
